import { useEffect, useMemo, useState } from 'react';
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiFilter,
  FiTarget,
  FiUpload,
  FiDownload,
  FiActivity,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Select,
  MultiSelect,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  CostCenterForm,
  CostCenterImportModal,
  Tooltip,
} from '../../components';
import TableActions from '../../components/Table/TableActions';

import '../CompanyManager/style.css';

export default function CostCenterManager() {
  const permissions = usePermission('financial');
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [costCenterToDelete, setCostCenterToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  useEffect(() => {
    if (permissions.canView) {
      loadCompanies();
    }
  }, [permissions.canView]);

  useEffect(() => {
    if (permissions.canView) {
      loadCostCenters(selectedCompanyFilter || 'all');
    }
  }, [selectedCompanyFilter, permissions.canView]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompanyFilter]);

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Erro ao carregar empresas.');
    }
  }

  async function loadCostCenters(companyId) {
    try {
      setLoading(true);
      const data = await Api.getCostCenters(companyId);

      if (Array.isArray(data)) {
        setCostCenters(data);
        setHasLoadedOnce(true);
      } else {
        setError('Não foi possível carregar os centros de custo.');
      }
    } catch (err) {
      setError('Erro ao carregar centros de custo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleExport() {
    if (!costCenters || costCenters.length === 0) {
      toast.warning('Não há dados para exportar.');
      return;
    }

    const exportData = costCenters.map(c => ({
      'Código': c.code || '',
      'Nome': c.name,
      'Empresa': c.company?.trade_name || c.company?.corporate_name || '',
      'Descrição': c.description || '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Centros de Custo');
    XLSX.writeFile(wb, 'centros_de_custo.xlsx');
  }

  function handleCreate() {
    if (companies.length === 0) loadCompanies();
    setSelectedCostCenter(null);
    setIsModalOpen(true);
  }

  function handleEdit(costCenter) {
    if (companies.length === 0) loadCompanies();
    setSelectedCostCenter(costCenter);
    setIsModalOpen(true);
  }

  function handleFormSuccess() {
    loadCostCenters(selectedCompanyFilter || 'all');
    setIsModalOpen(false);
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setSelectedCompanyFilter(idsWithoutAll[0]);
    } else {
      setSelectedCompanyFilter('');
    }
  }

  function handleDeleteClick(costCenter) {
    setCostCenterToDelete(costCenter);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!costCenterToDelete) return;

    try {
      setIsDeleting(true);
      await Api.deleteCostCenter(costCenterToDelete.company_id, costCenterToDelete.id);
      loadCostCenters(selectedCompanyFilter || 'all');
      setIsDeleteModalOpen(false);
      setCostCenterToDelete(null);
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir centro de custo.');
    } finally {
      setIsDeleting(false);
    }
  }

  const filteredCostCenters = useMemo(() => {
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return costCenters.filter(costCenter => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        costCenter.name?.toLowerCase().includes(searchLower) ||
        costCenter.code?.toLowerCase().includes(searchLower) ||
        costCenter.description?.toLowerCase().includes(searchLower);

      const companyId = costCenter.company_id || costCenter.company?.id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesCompany;
    });
  }, [costCenters, searchTerm, companyFilterValues]);

  const totalItems = filteredCostCenters.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedCostCenters = filteredCostCenters.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'code',
      header: 'Código',
      width: '1fr',
      cellClassName: 'manager-table-cell-primary',
      render: costCenter => (
        <>
          <div className="manager-table-avatar">
            <FiTarget size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {costCenter.code || '-'}
            </span>
          </div>
        </>
      ),
    },
    {
      id: 'name',
      header: 'Nome',
      width: '2fr',
      cellClassName: 'manager-table-cell-primary',
      render: costCenter => (
        <div className="manager-table-cell-text">
          <span className="manager-table-title">
            {costCenter.name}
          </span>
        </div>
      ),
    },
    {
      id: 'description',
      header: 'Descrição',
      width: '3fr',
      cellClassName: 'manager-table-cell',
      render: costCenter => (
        <span className="manager-table-subtitle">
          {costCenter.description || '-'}
        </span>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: costCenter => (
        <span className="manager-table-text">
          {costCenter.company?.trade_name || costCenter.company?.corporate_name || '-'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: costCenter => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(costCenter);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(costCenter);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  function CostCenterSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  const isMobile = window.innerWidth <= 768;

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiActivity size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <CostCenterSkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiTarget size={22} />
                </div>
                <div>
                  <h1>Centros de Custo</h1>
                  <p>
                    Visualize e gerencie os centros de custo da empresa selecionada.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  variant="secondary"
                  className="manager-header-button"
                  onClick={handleExport}
                >
                  <FiDownload size={18} />
                  {!isMobile && 'Exportar'}
                </Button>
                
                {permissions.canCreate && (
                  <Button
                    variant="secondary"
                    className="manager-header-button"
                    onClick={() => {
                      if (companies.length === 0) loadCompanies();
                      setIsImportModalOpen(true);
                    }}
                  >
                    <FiUpload size={18} />
                    {!isMobile && 'Importar'}
                  </Button>
                )}

                {permissions.canCreate && (
                  <Button onClick={handleCreate} className="manager-header-button">
                    <FiPlus size={18} />
                    Centro de Custo
                  </Button>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {costCenters.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredCostCenters.length} de {costCenters.length} registros
                  </span>
                )}
              </div>

              <div className='manager-filters-grid-custom'>
                <div style={{ flex: 1 }}>
                  <MultiSelect
                    id="companyFilter"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({ value: String(c.id), label: c.trade_name || c.corporate_name }))
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                    placeholder="Filtrar por empresa"
                    label={"Empresa"}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    id="search"
                    label="Buscar"
                    placeholder="Buscar por nome ou código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    icon={FiSearch}
                  />
                </div>
              </div>
            </Card>

            {error ? (
              <div className="manager-error-container">
                <p>{error}</p>
                <Button onClick={() => loadCostCenters(selectedCompanyFilter || 'all')}>
                  Tentar Novamente
                </Button>
              </div>
            ) : (
              <Table
                items={paginatedCostCenters}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                getRowKey={(costCenter) => costCenter.id}
                emptyMessage="Nenhum centro de custo encontrado."
              />
            )}
          </>
        )}
      </div>

      {isModalOpen && (
        <Modal
          title={selectedCostCenter ? 'Editar Centro de Custo' : 'Novo Centro de Custo'}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <CostCenterForm
              initialData={selectedCostCenter}
              companies={companies}
              companyId={selectedCompanyFilter}
              onSuccess={handleFormSuccess}
              onCancel={() => setIsModalOpen(false)}
            />
        </Modal>
      )}

      {isDeleteModalOpen && (
        <Modal
          title="Confirmar Exclusão"
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <div className="delete-modal-content">
            <p>
              Tem certeza que deseja excluir o centro de custo{' '}
              <span className="delete-modal-company-name">
                {costCenterToDelete?.name}
              </span>
              ?
            </p>
            <p>Essa ação não poderá ser desfeita.</p>
          </div>
          <div className="delete-modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        </Modal>
      )}

      {isImportModalOpen && (
        <CostCenterImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onSuccess={handleFormSuccess}
          companyId={selectedCompanyFilter}
          companies={companies}
        />
      )}
    </PageComponent>
  );
}
